import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../../utils/apiFetch';
import apiPath from '../../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import { StatusBadge, Flag, fmtDay, BORDER, errMsg } from './adminUi';
import { ConfirmModal } from './AdminActionModals';

const TABS = [
  ['All', 'All'],
  ['Pending Empivo Review', 'Needs Review'],
  ['Information Requested', 'Info Requested'],
  ['Approved', 'Approved'],
  ['Submitted to Everee', 'With Everee'],
  ['Active', 'Active'],
  ['Rejected', 'Rejected'],
];

const AdminPayrollList = ({ onOpen }) => {
  const notification = useToastContext();
  const [status, setStatus] = useState('All');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ records: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [toggleRow, setToggleRow] = useState(null); // row whose payroll service switch is being confirmed
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await apiGet(`${apiPath.payrollAdminBase}/list?status=${encodeURIComponent(status)}&search=${encodeURIComponent(search)}`);
        if (cancelled) return;
        if (res?.data?.success) setData(res.data.results);
        else notification.error(errMsg(res, 'Could not load onboardings'));
      } catch (e) {
        if (!cancelled) notification.error('Could not load onboardings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0); // debounce typing
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, reloadKey]);

  const confirmToggle = async () => {
    const turnOn = toggleRow.payrollService !== 'active';
    try {
      setToggling(true);
      const res = await apiPut(`${apiPath.payrollAdminBase}/${toggleRow._id}/credentials/status`, { status: turnOn ? 'active' : 'inactive' });
      if (res?.data?.success) {
        notification.success(turnOn ? 'Payroll service activated' : 'Payroll service deactivated');
        setToggleRow(null);
        setReloadKey((k) => k + 1); // refresh rows + tiles
      } else {
        notification.error(errMsg(res, 'Could not change the payroll service'));
      }
    } catch (e) {
      notification.error('Could not change the payroll service');
    } finally {
      setToggling(false);
    }
  };

  const { records, counts } = data;
  const needsAction = (counts['Pending Empivo Review'] || 0) + (counts['Information Requested'] || 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Payroll Administration</p>
          <h1 className="text-2xl font-bold text-gray-900">Client Payroll Onboardings</h1>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search company or EIN…"
          className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 sm:w-72"
        />
      </div>

      {/* summary tiles */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ['Awaiting review', needsAction, 'text-amber-700'],
          ['Approved – ready to send', counts.Approved || 0, 'text-blue-700'],
          ['With Everee', counts['Submitted to Everee'] || 0, 'text-indigo-700'],
          ['Active', counts.Active || 0, 'text-green-700'],
        ].map(([label, n, color]) => (
          <div key={label} className={`border ${BORDER} bg-white p-4`}>
            <p className={`text-3xl font-bold tabular-nums ${color}`}>{n}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* filter tabs */}
      <div className="mb-3 flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${
              status === value ? 'border-blue-700 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {label} <span className="ml-1 text-xs text-gray-400">{counts[value] || 0}</span>
          </button>
        ))}
      </div>

      {/* table */}
      <div className={`overflow-x-auto border ${BORDER} bg-white`}>
        <table className="w-full min-w-[940px] text-left text-sm">
          <thead className="bg-[#e6e3de] text-xs font-bold uppercase tracking-wider text-gray-700">
            <tr>
              {['Company', 'Submitted', 'Workers', 'Docs', 'Agreement', 'Payment', 'Status', 'Payroll service'].map((h) => (
                <th key={h} className="px-4 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No onboardings found.</td></tr>
            )}
            {!loading && records.map((r) => (
              <tr key={r._id} onClick={() => onOpen(r._id)} className="cursor-pointer border-t border-gray-100 hover:bg-blue-50/40">
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-900">{r.legalName}</p>
                  <p className="text-xs text-gray-500">EIN {r.ein}</p>
                </td>
                <td className="px-4 py-3 text-gray-700">{fmtDay(r.createdAt)}</td>
                <td className="px-4 py-3 text-gray-700">{r.w2EmployeeCount || 0} W-2 · {r.contractorCount || 0} 1099</td>
                <td className="px-4 py-3"><Flag ok={r.readiness.requiredDocuments} yes="Complete" no="Missing" /></td>
                <td className="px-4 py-3"><Flag ok={r.readiness.agreementSigned} yes="Signed" no="Unsigned" /></td>
                <td className="px-4 py-3"><Flag ok={r.readiness.paymentPaid} yes="Paid" no="Unpaid" /></td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    const on = r.payrollService === 'active';
                    const canToggle = ['Submitted to Everee', 'Active'].includes(r.status) && r.payrollService !== 'not_set' && (on || r.payrollServiceVerified);
                    const hint = r.payrollService === 'not_set'
                      ? 'Save the Everee credentials first (open the record)'
                      : !on && !r.payrollServiceVerified
                      ? 'Credentials are not verified – open the record and re-save them'
                      : !['Submitted to Everee', 'Active'].includes(r.status)
                      ? 'Available once the package is sent to Everee'
                      : on ? 'Turn payroll service off' : 'Turn payroll service on';
                    return (
                      <button
                          type="button"
                          role="switch"
                          aria-checked={on}
                          aria-label={`Payroll service for ${r.legalName}`}
                          title={hint}
                          disabled={!canToggle}
                          onClick={() => setToggleRow(r)}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:!opacity-40 ${on ? '!bg-green-600' : '!bg-gray-300'}`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toggleRow && (
        <ConfirmModal
          title={toggleRow.payrollService === 'active' ? 'Deactivate payroll service' : 'Activate payroll service'}
          body={
            toggleRow.payrollService === 'active'
              ? `Turn off payroll for ${toggleRow.legalName}? They will no longer be able to use the payroll service.`
              : `Turn on payroll for ${toggleRow.legalName}? Empivo will use the saved tenant ID and API token to call Everee on their behalf.`
          }
          confirmLabel={toggleRow.payrollService === 'active' ? 'Deactivate' : 'Activate'}
          variant={toggleRow.payrollService === 'active' ? 'danger' : 'success'}
          busy={toggling}
          onConfirm={confirmToggle}
          onClose={() => setToggleRow(null)}
        />
      )}
    </div>
  );
};

export default AdminPayrollList;





// import React, { useEffect, useState } from 'react';
// import { apiGet } from '../../../utils/apiFetch';
// import apiPath from '../../../utils/pathObj';
// import useToastContext from 'hooks/useToastContext';
// import { StatusBadge, Flag, fmtDay, BORDER, errMsg } from './adminUi';

// const TABS = [
//   ['All', 'All'],
//   ['Pending Empivo Review', 'Needs Review'],
//   ['Information Requested', 'Info Requested'],
//   ['Approved', 'Approved'],
//   ['Submitted to Everee', 'With Everee'],
//   ['Active', 'Active'],
//   ['Rejected', 'Rejected'],
// ];

// const AdminPayrollList = ({ onOpen }) => {
//   const notification = useToastContext();
//   const [status, setStatus] = useState('All');
//   const [search, setSearch] = useState('');
//   const [data, setData] = useState({ records: [], counts: {} });
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     let cancelled = false;
//     const t = setTimeout(async () => {
//       try {
//         setLoading(true);
//         const res = await apiGet(`${apiPath.payrollAdminBase}/list?status=${encodeURIComponent(status)}&search=${encodeURIComponent(search)}`);
//         if (cancelled) return;
//         if (res?.data?.success) setData(res.data.results);
//         else notification.error(errMsg(res, 'Could not load onboardings'));
//       } catch (e) {
//         if (!cancelled) notification.error('Could not load onboardings');
//       } finally {
//         if (!cancelled) setLoading(false);
//       }
//     }, search ? 300 : 0); // debounce typing
//     return () => { cancelled = true; clearTimeout(t); };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [status, search]);

//   const { records, counts } = data;
//   const needsAction = (counts['Pending Empivo Review'] || 0) + (counts['Information Requested'] || 0);

//   return (
//     <div className="mx-auto max-w-6xl">
//       <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
//         <div>
//           <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Payroll Administration</p>
//           <h1 className="text-2xl font-bold text-gray-900">Client Payroll Onboardings</h1>
//         </div>
//         <input
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//           placeholder="Search company or EIN…"
//           className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 sm:w-72"
//         />
//       </div>

//       {/* summary tiles */}
//       <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
//         {[
//           ['Awaiting review', needsAction, 'text-amber-700'],
//           ['Approved – ready to send', counts.Approved || 0, 'text-blue-700'],
//           ['With Everee', counts['Submitted to Everee'] || 0, 'text-indigo-700'],
//           ['Active', counts.Active || 0, 'text-green-700'],
//         ].map(([label, n, color]) => (
//           <div key={label} className={`border ${BORDER} bg-white p-4`}>
//             <p className={`text-3xl font-bold tabular-nums ${color}`}>{n}</p>
//             <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
//           </div>
//         ))}
//       </div>

//       {/* filter tabs */}
//       <div className="mb-3 flex flex-wrap gap-1 border-b border-gray-200">
//         {TABS.map(([value, label]) => (
//           <button
//             key={value}
//             type="button"
//             onClick={() => setStatus(value)}
//             className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${
//               status === value ? 'border-blue-700 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
//             }`}
//           >
//             {label} <span className="ml-1 text-xs text-gray-400">{counts[value] || 0}</span>
//           </button>
//         ))}
//       </div>

//       {/* table */}
//       <div className={`overflow-x-auto border ${BORDER} bg-white`}>
//         <table className="w-full min-w-[940px] text-left text-sm">
//           <thead className="bg-[#e6e3de] text-xs font-bold uppercase tracking-wider text-gray-700">
//             <tr>
//               {['Company', 'Submitted', 'Workers', 'Docs', 'Agreement', 'Payment', 'Status', 'Payroll service'].map((h) => (
//                 <th key={h} className="px-4 py-2.5">{h}</th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {loading && (
//               <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
//             )}
//             {!loading && records.length === 0 && (
//               <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No onboardings found.</td></tr>
//             )}
//             {!loading && records.map((r) => (
//               <tr key={r._id} onClick={() => onOpen(r._id)} className="cursor-pointer border-t border-gray-100 hover:bg-blue-50/40">
//                 <td className="px-4 py-3">
//                   <p className="font-semibold text-gray-900">{r.legalName}</p>
//                   <p className="text-xs text-gray-500">EIN {r.ein}</p>
//                 </td>
//                 <td className="px-4 py-3 text-gray-700">{fmtDay(r.createdAt)}</td>
//                 <td className="px-4 py-3 text-gray-700">{r.w2EmployeeCount || 0} W-2 · {r.contractorCount || 0} 1099</td>
//                 <td className="px-4 py-3"><Flag ok={r.readiness.requiredDocuments} yes="Complete" no="Missing" /></td>
//                 <td className="px-4 py-3"><Flag ok={r.readiness.agreementSigned} yes="Signed" no="Unsigned" /></td>
//                 <td className="px-4 py-3"><Flag ok={r.readiness.paymentPaid} yes="Paid" no="Unpaid" /></td>
//                 <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
//                 <td className="px-4 py-3">
//                   <span className={`text-xs font-semibold ${r.payrollService === 'active' ? 'text-green-700' : r.payrollService === 'inactive' ? 'text-gray-600' : 'text-gray-400'}`}>
//                     {r.payrollService === 'active' ? '● Active' : r.payrollService === 'inactive' ? 'Inactive' : 'Not set up'}
//                   </span>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default AdminPayrollList;